import React, { useState, useEffect } from 'react';
import { adminApi, interviewAdminApi } from '../../services/adminApi';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/ConfirmDialog';

const formatPhone = (phone: string) => {
  const d = phone.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  if (d.length === 10) return d.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
  return phone;
};

const formatElapsed = (start: string, end: string): string => {
  const diff = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (diff < 60) return `${diff}초`;
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  if (m < 60) return s > 0 ? `${m}분 ${s}초` : `${m}분`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}시간 ${rm}분` : `${h}시간`;
};

const statusLabels: Record<string, string> = {
  DRAFT: '작성중', APPLIED: '지원완료', AI_REVIEWED: 'AI심사완료',
  WRITTEN_TEST: '서면평가', INTERVIEW: '면접', OFFER: '오퍼',
  HIRED: '입사확정', DOC_REJECTED: '서류불합격', WRITTEN_REJECTED: '서면불합격', REJECTED: '불합격',
};

// statusFlow는 상태 관리 섹션 제거로 미사용

interface Props {
  id: number;
  onBack: () => void;
  admin: any;
  viewMode?: 'full' | 'hired';
}

const ApplicantDetail: React.FC<Props> = ({ id, onBack, admin, viewMode = 'full' }) => {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [evaluatingId, setEvaluatingId] = useState<number | null>(null);
  const [evalScore, setEvalScore] = useState('');
  const [evalComment, setEvalComment] = useState('');
  const [essayOpen, setEssayOpen] = useState(false);
  const isDev = admin?.email === 'sungbae@grkcon.com';

  useEffect(() => { loadDetail(true); }, [id]);

  const loadDetail = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const result = await adminApi.getApplicantDetail(id);
      setData(result);
    } catch {}
    if (showLoading) setLoading(false);
  };

  const handleStatusChange = async (status: string, reason?: string) => {
    const isReject = status.includes('REJECTED') || status === 'REJECTED';
    const ok = await confirm({
      title: `상태를 "${statusLabels[status]}"로 변경하시겠습니까?`,
      confirmText: '변경',
      variant: isReject ? 'danger' : 'default',
    });
    if (!ok) return;
    setStatusLoading(true);
    try {
      await adminApi.updateStatus(id, status, reason);
      toast.success('상태가 변경되었습니다.');
      loadDetail();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '변경 실패');
    }
    setStatusLoading(false);
  };

  const handleAiEvaluation = async () => {
    setAiLoading(true);
    try {
      await adminApi.runAiEvaluation(id);
      toast.success('AI 평가가 완료되었습니다.');
      loadDetail();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'AI 평가 실패');
    }
    setAiLoading(false);
  };

  const handleEvaluateTest = async (applicantTestId: number) => {
    const score = parseInt(evalScore);
    if (isNaN(score) || score < 0 || score > 100) {
      toast.error('점수는 0~100 사이로 입력해주세요.');
      return;
    }
    try {
      await adminApi.evaluateTest(applicantTestId, evalComment, score);
      toast.success('평가가 저장되었습니다.');
      setEvaluatingId(null);
      setEvalScore('');
      setEvalComment('');
      loadDetail();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '평가 저장 실패');
    }
  };

  const handleResetTest = async (applicantTestId: number) => {
    const ok = await confirm({
      title: '시험을 리셋하시겠습니까?',
      message: '기존 답변이 모두 삭제되고 지원자가 재응시할 수 있습니다.',
      confirmText: '리셋',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await adminApi.resetApplicantTest(applicantTestId);
      toast.success('시험이 리셋되었습니다.');
      loadDetail();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '리셋 실패');
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">로딩 중...</p>
      </div>
    );
  }

  const { applicant, aiEvaluation, applicantTests, interview, offer, campaign } = data;
  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={onBack} className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-base font-semibold text-slate-800">
            {applicant.name || '이름없음'} ({applicant.email})
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* 기본 정보 */}
        <Section title="기본 정보">
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label="이름" value={applicant.name || '-'} />
            <InfoRow label="이메일" value={applicant.email} />
            <InfoRow label="연락처" value={applicant.phone ? formatPhone(applicant.phone) : '-'} />
            <InfoRow label="상태" value={statusLabels[applicant.status] || applicant.status} />
            <InfoRow label="제출일" value={applicant.submitted_at ? new Date(applicant.submitted_at).toLocaleString('ko-KR') : '미제출'} />
            <InfoRow label="이력서" value={applicant.resume_url ? '업로드됨' : '없음'} />
          </div>
          {applicant.resume_url && (
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('hire_admin_token');
                  const res = await fetch(`/api${applicant.resume_url}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!res.ok) throw new Error('다운로드 실패');
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${applicant.name || 'resume'}_이력서.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch {
                  toast.error('이력서 다운로드에 실패했습니다.');
                }
              }}
              className="inline-block mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer"
            >
              이력서 다운로드
            </button>
          )}
          {applicant.essay && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <button
                onClick={() => setEssayOpen(v => !v)}
                className="flex items-center justify-between w-full text-left"
              >
                <span className="text-xs font-semibold text-slate-600">에세이</span>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${essayOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {essayOpen && (
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-lg p-4">
                  {applicant.essay}
                </p>
              )}
            </div>
          )}
        </Section>

        {/* 최종합격 탭 뷰: 오퍼 → 입사서류 체크리스트 */}
        {viewMode === 'hired' && (
          <>
            <OfferSection
              applicant={applicant}
              offer={offer}
              campaign={campaign}
              onUpdate={() => loadDetail(true)}
            />
            {offer?.reference_check_requested && (
              <ReferenceConsentSection applicant={applicant} offer={offer} />
            )}
            <OnboardingSection applicant={applicant} toast={toast} />
          </>
        )}
        {(applicant.status === 'DOC_REJECTED' || applicant.status === 'WRITTEN_REJECTED' || applicant.status === 'REJECTED') && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <p className="text-sm text-red-600 font-medium">불합격 처리된 지원자입니다.</p>
          </div>
        )}

        {/* AI 평가, 서면평가, 면접 (최종합격 탭에서는 숨김) */}
        {viewMode !== 'hired' && <>
        <Section title="AI 평가">
          {aiEvaluation ? (
            <div className="space-y-4">
              {/* 에세이 평가 */}
              <div>
                <h3 className="text-xs font-semibold text-indigo-600 mb-2 uppercase tracking-wide">에세이 평가</h3>
                <div className="grid grid-cols-3 gap-3 mb-2">
                  <ScoreCard label="지원동기" score={aiEvaluation.essay_motivation_score} />
                  <ScoreCard label="논리력" score={aiEvaluation.essay_logic_score} />
                  <ScoreCard label="자기이해" score={aiEvaluation.essay_self_score} />
                </div>
                {aiEvaluation.essay_comment && (
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{aiEvaluation.essay_comment}</p>
                )}
              </div>

              <hr className="border-slate-200" />

              {/* 이력서 평가 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">이력서 평가</h3>
                  {aiEvaluation.resume_comment === '이력서 평가 실패' && (
                    <button
                      onClick={handleAiEvaluation}
                      disabled={aiLoading}
                      className="px-2.5 py-1 text-xs rounded-lg font-medium bg-amber-50 text-amber-600 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {aiLoading ? '평가 중...' : '⚠ 재평가'}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 mb-2">
                  <ScoreCard label="직무적합" score={aiEvaluation.resume_fit_score} />
                  <ScoreCard label="경력역량" score={aiEvaluation.resume_career_score} />
                  <ScoreCard label="성장잠재" score={aiEvaluation.resume_growth_score} />
                </div>
                {aiEvaluation.resume_comment && (
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{aiEvaluation.resume_comment}</p>
                )}
              </div>

              <hr className="border-slate-200" />

              {/* 종합 점수 */}
              <div className="text-center py-2">
                <p className="text-xs text-slate-500 mb-1">종합</p>
                <p className={`text-2xl font-bold ${aiEvaluation.total_score >= 70 ? 'text-green-600' : aiEvaluation.total_score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                  {aiEvaluation.total_score}점
                </p>
              </div>

              {(() => {
                const hasTestActivity = applicantTests && applicantTests.some(
                  (t: any) => t.status === 'SUBMITTED' || t.status === 'EVALUATED' || t.status === 'IN_PROGRESS'
                );
                return (
                  <div className="flex items-center justify-end">
                    {applicant.status === 'DOC_REJECTED' ? (
                      <button onClick={() => handleStatusChange('AI_REVIEWED')}
                        className="px-3 py-1.5 text-xs rounded-lg font-medium bg-slate-100 text-slate-600 hover:bg-slate-200">
                        불합격 취소
                      </button>
                    ) : applicant.status === 'WRITTEN_TEST' && !hasTestActivity ? (
                      <button onClick={() => handleStatusChange('AI_REVIEWED')}
                        className="px-3 py-1.5 text-xs rounded-lg font-medium bg-slate-100 text-slate-600 hover:bg-slate-200">
                        합격 취소
                      </button>
                    ) : applicant.status === 'AI_REVIEWED' ? (
                      <div className="flex gap-2">
                        <button onClick={() => handleStatusChange('WRITTEN_TEST')} disabled={statusLoading}
                          className="px-3 py-1.5 text-xs rounded-lg font-medium bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50">
                          {statusLoading ? '처리 중...' : '서류 합격'}
                        </button>
                        <button onClick={() => handleStatusChange('DOC_REJECTED')} disabled={statusLoading}
                          className="px-3 py-1.5 text-xs rounded-lg font-medium bg-orange-50 text-orange-600 hover:bg-orange-100 disabled:opacity-50">
                          {statusLoading ? '처리 중...' : '서류 불합격'}
                        </button>
                        <button onClick={() => handleStatusChange('DOC_REJECTED', 'EXPIRED')} disabled={statusLoading}
                          className="px-3 py-1.5 text-xs rounded-lg font-medium bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50">
                          {statusLoading ? '처리 중...' : '기한경과 불합격'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="text-center py-4">
              {applicant.is_submitted ? (
                <div className="flex flex-col items-center gap-2">
                  {applicant.status === 'APPLIED' ? (
                    <>
                      <p className="text-sm text-amber-600 font-medium">AI 평가가 완료되지 않았습니다.</p>
                      <button
                        onClick={handleAiEvaluation}
                        disabled={aiLoading}
                        className="px-4 py-2 text-sm rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {aiLoading ? 'AI 평가 실행 중...' : 'AI 평가 재실행'}
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <p className="text-sm text-indigo-600 font-medium">AI 심사 중...</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500">지원서가 제출된 후 AI 심사가 자동으로 진행됩니다.</p>
              )}
            </div>
          )}
        </Section>

        {/* 서면평가 */}
        <Section title="서면평가">
          {applicantTests && applicantTests.length > 0 ? (
            <div className="space-y-3">
              {applicantTests.map((t: any) => (
                <div key={t.id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        t.test?.type === 'LOGIC' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {t.test?.type === 'LOGIC' ? '논리력' : '케이스'}
                      </span>
                      <span className="text-sm font-medium text-slate-700">{t.test?.title}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      t.status === 'SUBMITTED' ? 'bg-green-100 text-green-700'
                        : t.status === 'EVALUATED' ? 'bg-indigo-100 text-indigo-700'
                        : t.status === 'IN_PROGRESS' ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      {t.status === 'NOT_STARTED' ? '미시작' : t.status === 'IN_PROGRESS' ? '진행중' : t.status === 'SUBMITTED' ? '제출완료' : '평가완료'}
                    </span>
                  </div>
                  {t.test?.type === 'LOGIC' ? (
                    /* 논리력: 자동채점 + 소요시간 + 제출일 */
                    <>
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                          <span className="text-slate-400">점수</span>
                          <p className="text-slate-700 font-medium">{t.score !== null ? `${t.score}점` : '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-400">소요시간</span>
                          <p className="text-slate-700 font-medium">{t.started_at && t.submitted_at ? formatElapsed(t.started_at, t.submitted_at) : '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-400">제출일</span>
                          <p className="text-slate-700 font-medium">{t.submitted_at ? new Date(t.submitted_at).toLocaleDateString('ko-KR') : '-'}</p>
                        </div>
                      </div>
                      {isDev && (t.status === 'SUBMITTED' || t.status === 'EVALUATED' || t.status === 'IN_PROGRESS') && (
                        <div className="mt-2 pt-2 border-t border-slate-200">
                          <button onClick={() => handleResetTest(t.id)}
                            className="text-xs text-red-500 hover:text-red-600 font-medium">
                            재시험
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    /* 케이스 스터디: 관리자 평가 + 소요시간 + 제출일 */
                    <>
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                          <span className="text-slate-400">평가 점수</span>
                          <p className="text-slate-700 font-medium">{t.evaluation_score !== null ? `${t.evaluation_score}점` : '미채점'}</p>
                        </div>
                        <div>
                          <span className="text-slate-400">소요시간</span>
                          <p className="text-slate-700 font-medium">{t.started_at && t.submitted_at ? formatElapsed(t.started_at, t.submitted_at) : '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-400">제출일</span>
                          <p className="text-slate-700 font-medium">{t.submitted_at ? new Date(t.submitted_at).toLocaleDateString('ko-KR') : '-'}</p>
                        </div>
                      </div>
                      {/* 지원자 답변 */}
                      {t.answers && t.answers.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                          {t.answers.map((ans: any, idx: number) => (
                            <div key={ans.id} className="bg-white rounded-lg p-3 border border-slate-200 space-y-2">
                              <div className="pb-2 border-b border-slate-100">
                                <span className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wider">Question {idx + 1}</span>
                                <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{ans.question?.question || '질문'}</p>
                              </div>
                              <div>
                                <span className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">Answer</span>
                                <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{ans.answer || '(미응답)'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {t.evaluation && (
                        <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">{t.evaluation}</p>
                      )}
                      {(t.status === 'SUBMITTED' || t.status === 'EVALUATED') && (
                        <div className="mt-2 pt-2 border-t border-slate-200">
                          {evaluatingId === t.id ? (
                            <div className="space-y-2">
                              <input type="number" min="0" max="100" value={evalScore}
                                onChange={(e) => setEvalScore(e.target.value)}
                                placeholder="점수 (0~100)"
                                className="w-28 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                              <textarea value={evalComment}
                                onChange={(e) => setEvalComment(e.target.value)}
                                placeholder="평가 코멘트를 입력하세요..."
                                rows={2}
                                className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
                              <div className="flex gap-2">
                                <button onClick={() => handleEvaluateTest(t.id)}
                                  className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                                  저장
                                </button>
                                <button onClick={() => { setEvaluatingId(null); setEvalScore(''); setEvalComment(''); }}
                                  className="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">
                                  취소
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <button onClick={() => { setEvaluatingId(t.id); setEvalScore(t.evaluation_score?.toString() || ''); setEvalComment(t.evaluation || ''); }}
                                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                                {t.status === 'EVALUATED' ? '재채점' : '채점하기'}
                              </button>
                              {isDev && (
                                <button onClick={() => handleResetTest(t.id)}
                                  className="text-xs text-red-500 hover:text-red-600 font-medium">
                                  재시험
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {/* 합격/불합격 버튼 */}
              {(() => {
                const allGraded = applicantTests.every((t: any) =>
                  t.test?.type === 'LOGIC'
                    ? (t.status === 'SUBMITTED' || t.status === 'EVALUATED')
                    : t.status === 'EVALUATED'
                );
                return (
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 mt-3">
                    {applicant.status === 'INTERVIEW' ? (
                      <button onClick={() => handleStatusChange('WRITTEN_TEST')}
                        className="px-3 py-1.5 text-xs rounded-lg font-medium bg-slate-100 text-slate-600 hover:bg-slate-200">
                        합격 취소
                      </button>
                    ) : applicant.status === 'WRITTEN_REJECTED' ? (
                      <button onClick={() => handleStatusChange('WRITTEN_TEST')}
                        className="px-3 py-1.5 text-xs rounded-lg font-medium bg-slate-100 text-slate-600 hover:bg-slate-200">
                        불합격 취소
                      </button>
                    ) : applicant.status === 'WRITTEN_TEST' ? (
                      <>
                        {allGraded && (
                          <button onClick={() => handleStatusChange('INTERVIEW')}
                            className="px-3 py-1.5 text-xs rounded-lg font-medium bg-green-50 text-green-600 hover:bg-green-100">
                            서면평가 합격
                          </button>
                        )}
                        <button onClick={() => handleStatusChange('WRITTEN_REJECTED')}
                          className="px-3 py-1.5 text-xs rounded-lg font-medium bg-red-50 text-red-600 hover:bg-red-100">
                          서면평가 불합격
                        </button>
                        <button onClick={() => handleStatusChange('WRITTEN_REJECTED', 'EXPIRED')}
                          className="px-3 py-1.5 text-xs rounded-lg font-medium bg-orange-50 text-orange-600 hover:bg-orange-100">
                          기한경과 불합격
                        </button>
                      </>
                    ) : null}
                  </div>
                );
              })()}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">서면평가 내역이 없습니다.</p>
          )}
        </Section>

        {/* 면접 */}
        <InterviewSection
          applicant={applicant}
          interview={interview}
          onUpdate={loadDetail}
        />
        </>}

        {/* 오퍼 (최종합격 탭이 아닐 때 — 최종합격 탭은 상단에서 표시) */}
        {viewMode !== 'hired' && (
          <>
            <OfferSection
              applicant={applicant}
              offer={offer}
              campaign={campaign}
              onUpdate={() => loadDetail(true)}
            />
            {offer?.reference_check_requested && (
              <ReferenceConsentSection applicant={applicant} offer={offer} />
            )}
          </>
        )}

        {/* 전체 지원자 탭: HIRED면 하단에 입사서류 체크리스트 */}
        {viewMode === 'full' && applicant.status === 'HIRED' && (
          <OnboardingSection applicant={applicant} toast={toast} />
        )}
      </main>
    </div>
  );
};

// 서류 승인 + 이메일 설정 폼
const ApprovalForm: React.FC<{
  applicant: any;
  onApproved: () => void;
  toast: any;
  confirm: any;
}> = ({ applicant, onApproved, toast, confirm }) => {
  const [companyEmail, setCompanyEmail] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggested, setSuggested] = useState(false);

  useEffect(() => {
    adminApi.suggestEmail(applicant.name).then((data: any) => {
      if (data.suggestedEmail) {
        setCompanyEmail(data.suggestedEmail);
        setSuggested(true);
      }
      // 영문이름 자동추천: firstName + lastName
      if (data.firstName && data.lastName) {
        setNameEn(`${data.firstName} ${data.lastName}`);
      }
    }).catch(() => {});
  }, [applicant.name]);

  const handleApprove = async () => {
    if (!companyEmail || !companyEmail.includes('@')) {
      toast.error('회사 이메일을 입력해주세요.');
      return;
    }
    const ok = await confirm({
      title: '서류 승인',
      message: `${applicant.name}님의 입사 서류를 승인하시겠습니까?\n회사 이메일: ${companyEmail}\n영문 이름: ${nameEn || '(미입력)'}`,
      confirmText: '승인',
    });
    if (!ok) return;
    setLoading(true);
    try {
      await adminApi.approveOnboarding(applicant.id, companyEmail, nameEn);
      toast.success('서류가 승인되었습니다. workspace 직원 등록이 완료되었습니다.');
      onApproved();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '승인에 실패했습니다.');
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
        <p className="text-sm font-medium text-amber-800">서류 확인 후 승인해주세요</p>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            회사 이메일 <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={companyEmail}
            onChange={(e) => setCompanyEmail(e.target.value)}
            placeholder="예: sehyeon@grkcon.com"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {suggested && (
            <p className="text-xs text-slate-400 mt-1">이름 기반 자동 추천</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            영문 이름 <span className="text-slate-400 font-normal">(명함용)</span>
          </label>
          <input
            type="text"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder="예: Sehyeon Lee"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-xs text-slate-400 mt-1">자동 변환, 필요 시 수정</p>
        </div>
      </div>
      <button
        onClick={handleApprove}
        disabled={loading}
        className="w-full px-4 py-2.5 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
      >
        {loading ? '처리 중...' : '서류 승인 및 직원 등록'}
      </button>
    </div>
  );
};

// 온보딩 관리 섹션 (HIRED 상태)
const OnboardingSection: React.FC<{ applicant: any; toast: any }> = ({ applicant, toast }) => {
  const confirm = useConfirm();
  const [onboarding, setOnboarding] = useState<any>(null);
  const [obLoading, setObLoading] = useState(true);
  const [wsStatus, setWsStatus] = useState<any>(null);
  const [wsLoading, setWsLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'pdf' | 'image' | null>(null);
  const [previewLabel, setPreviewLabel] = useState<string>('');

  const DOCS = [
    { key: 'doc_diploma', label: '학력증명서', desc: '최종 학력 증명서' },
    { key: 'doc_career', label: '경력확인서류', desc: '급여통장사본 또는 건강보험득실확인서' },
    { key: 'doc_salary_contract', label: '직전직장 연봉계약서', desc: '직전 직장의 연봉계약서' },
    { key: 'doc_resident_copy', label: '주민등록등초본', desc: '주민등록등본 또는 초본' },
    { key: 'doc_bank_account', label: '본인명의 통장사본', desc: '급여 수령용 통장사본' },
    { key: 'doc_resume_kr', label: '이력서 한글본', desc: '한글 이력서' },
    { key: 'doc_resume_en', label: '이력서 영문본', desc: '영문 이력서' },
    { key: 'doc_profile_photo', label: '프로필 사진', desc: '흑백 여권사진 스타일' },
    { key: 'doc_intro_ppt', label: '컨설턴트 소개자료', desc: 'PPT 형식 소개자료' },
  ];

  const loadWsStatus = () => {
    adminApi.checkWorkspaceStatus(applicant.id).then(setWsStatus).catch(() => {});
  };

  useEffect(() => {
    adminApi.getApplicantDetail(applicant.id).then((data: any) => {
      if (data.onboarding) setOnboarding(data.onboarding);
      setObLoading(false);
    }).catch(() => setObLoading(false));
    loadWsStatus();
  }, [applicant.id]);

  if (obLoading) return <div className="text-center py-4 text-sm text-slate-500">온보딩 정보 로딩 중...</div>;

  const uploadedCount = DOCS.filter(d => onboarding?.[d.key]).length;

  const handleDownload = async (docKey: string, label: string) => {
    const filePath = onboarding?.[docKey];
    if (!filePath) return;
    try {
      const token = localStorage.getItem('hire_admin_token');
      const res = await fetch(`/api${filePath}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('다운로드 실패');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = filePath.split('.').pop() || 'pdf';
      a.download = `${applicant.name}_${label}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('파일 다운로드에 실패했습니다.');
    }
  };

  const handlePreview = async (docKey: string, label: string) => {
    const filePath = onboarding?.[docKey];
    if (!filePath) return;
    const ext = filePath.split('.').pop()?.toLowerCase() || '';
    const filename = filePath.split('/').pop() || '';

    try {
      const token = localStorage.getItem('hire_admin_token');
      const isImage = ['jpg', 'jpeg', 'png'].includes(ext);
      const isPdf = ext === 'pdf';

      // DOC/DOCX/PPT/PPTX: 서버에서 PDF로 변환 후 로드
      const fetchUrl = (isPdf || isImage)
        ? `/api${filePath}`
        : `/api/uploads/documents/${filename}/preview`;

      const res = await fetch(fetchUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('미리보기 실패');
      const arrayBuffer = await res.arrayBuffer();
      const mimeType = isImage ? `image/${ext === 'jpg' ? 'jpeg' : ext}` : 'application/pdf';
      const blob = new Blob([arrayBuffer], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewType(isImage ? 'image' : 'pdf');
      setPreviewLabel(label);
      document.body.style.overflow = 'hidden';
    } catch {
      toast.error('파일 미리보기에 실패했습니다.');
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewType(null);
    setPreviewLabel('');
    document.body.style.overflow = '';
  };

  // 신규 workspace 직원 등록은 '서류 승인 및 직원 등록'(approveOnboarding)으로 일원화.
  // 여기서는 상태 표시 + 관리(동기화/삭제/재등록)만 담당한다.

  const handleOpenSync = async () => {
    setWsLoading(true);
    try {
      const status = await adminApi.getWorkspaceSyncStatus(applicant.id);
      setSyncStatus(status);
      setShowSyncModal(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '동기화 상태 조회 실패');
    }
    setWsLoading(false);
  };

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const result = await adminApi.syncToWorkspace(applicant.id);
      toast.success(result.message || '동기화 완료');
      // 상태 갱신
      const status = await adminApi.getWorkspaceSyncStatus(applicant.id);
      setSyncStatus(status);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '동기화 실패');
    }
    setSyncLoading(false);
  };

  const handleRemoveWorkspace = async () => {
    const ok = await confirm({
      title: 'workspace 직원 삭제',
      message: `${applicant.name}님을 workspace에서 삭제하시겠습니까?`,
      confirmText: '삭제',
      variant: 'danger',
    });
    if (!ok) return;
    setWsLoading(true);
    try {
      const result = await adminApi.removeFromWorkspace(applicant.id);
      toast.success(result.message || '삭제 완료');
      loadWsStatus();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '삭제 실패');
    }
    setWsLoading(false);
  };

  const handleReRegisterWorkspace = async () => {
    const ok = await confirm({
      title: 'workspace 직원 재등록',
      message: `${applicant.name}님을 workspace에 재등록하시겠습니까?`,
      confirmText: '재등록',
    });
    if (!ok) return;
    setWsLoading(true);
    try {
      const result = await adminApi.pushToWorkspace(applicant.id);
      toast.success(result.message || '재등록 완료');
      loadWsStatus();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '재등록 실패');
    }
    setWsLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* 입사서류 체크리스트 */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-700">입사서류 체크리스트</h2>
          <span className={`text-sm font-medium px-3 py-1 rounded-full ${
            uploadedCount === DOCS.length
              ? 'bg-green-100 text-green-700'
              : 'bg-indigo-50 text-indigo-600'
          }`}>
            {uploadedCount}/{DOCS.length} 완료
          </span>
        </div>

        {/* 진행률 바 */}
        <div className="w-full bg-slate-200 rounded-full h-2 mb-5">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${uploadedCount === DOCS.length ? 'bg-green-500' : 'bg-indigo-500'}`}
            style={{ width: `${(uploadedCount / DOCS.length) * 100}%` }}
          />
        </div>

        <div className="space-y-1">
          {DOCS.map((doc, idx) => {
            const isUploaded = !!onboarding?.[doc.key];
            return (
              <div
                key={doc.key}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${
                  isUploaded ? 'bg-green-50' : 'bg-slate-50'
                }`}
              >
                {/* 체크 아이콘 */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isUploaded ? 'bg-green-500' : 'bg-slate-300'
                }`}>
                  {isUploaded ? (
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-xs text-white font-medium">{idx + 1}</span>
                  )}
                </div>

                {/* 서류 정보 */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isUploaded ? 'text-green-800' : 'text-slate-700'}`}>
                    {doc.label}
                    {isUploaded && (() => {
                      const filePath = onboarding?.[doc.key] || '';
                      const ext = filePath.split('.').pop()?.toUpperCase();
                      return ext ? <span className="ml-2 text-xs font-normal text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{ext}</span> : null;
                    })()}
                  </p>
                  <p className="text-xs text-slate-400">{doc.desc}</p>
                </div>

                {/* 미리보기 + 다운로드 버튼 */}
                {isUploaded && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => handlePreview(doc.key, doc.label)}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium px-2 py-1 rounded hover:bg-emerald-50"
                    >
                      미리보기
                    </button>
                    <button
                      onClick={() => handleDownload(doc.key, doc.label)}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium px-2 py-1 rounded hover:bg-indigo-50"
                    >
                      다운로드
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 대표이사 승인 */}
      {onboarding?.documents_completed && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          {onboarding.ceo_approved_at ? (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">서류 승인 완료</p>
                <p className="text-xs text-slate-400">
                  {new Date(onboarding.ceo_approved_at).toLocaleString('ko-KR')}
                </p>
              </div>
            </div>
          ) : (
            <ApprovalForm applicant={applicant} onApproved={() => {
              adminApi.getApplicantDetail(applicant.id).then((data: any) => {
                if (data.onboarding) setOnboarding(data.onboarding);
              });
              loadWsStatus();
            }} toast={toast} confirm={confirm} />
          )}
        </div>
      )}

      {/* 날인본 업로드 */}
      {onboarding?.ceo_approved_at && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">날인본 업로드</h2>
          {onboarding.signed_docs_sent_at ? (
            <div className="flex items-center gap-2 text-green-700 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              날인본 발송 완료 ({new Date(onboarding.signed_docs_sent_at).toLocaleString('ko-KR')})
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-3">
                근로계약서 · 개인정보 처리 동의서 · 영업비밀 보호 서약서 · 수습평가 동의서 · 전자기기 수령 확인서가
                모두 포함된 스캔본을 <strong>하나의 파일</strong>로 업로드하세요.<br />
                업로드하면 익일 오전 9시에 입사자 GRK 메일로 자동 발송됩니다.
              </p>
              {(() => {
                const isUploaded = !!onboarding.signed_docs;
                return (
                  <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isUploaded ? 'bg-green-50' : 'bg-slate-50'}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isUploaded ? 'bg-green-500' : 'bg-slate-300'}`}>
                      {isUploaded ? (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <span className="text-[10px] text-white">!</span>
                      )}
                    </div>
                    <span className={`flex-1 text-sm ${isUploaded ? 'text-green-800' : 'text-slate-700'}`}>
                      날인본 (5종 일괄)
                    </span>
                    <label className={`text-xs font-medium px-2 py-1 rounded cursor-pointer ${
                      isUploaded ? 'text-green-600 hover:bg-green-100' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}>
                      {isUploaded ? '재업로드' : '업로드'}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          await adminApi.uploadSignedDoc(applicant.id, 'signed_docs', file);
                          toast.success('날인본이 업로드되었습니다.');
                          adminApi.getApplicantDetail(applicant.id).then((data: any) => {
                            if (data.onboarding) setOnboarding(data.onboarding);
                          });
                        } catch (err: any) {
                          toast.error(err.response?.data?.message || '업로드에 실패했습니다.');
                        }
                        e.target.value = '';
                      }} />
                    </label>
                  </div>
                );
              })()}
              {onboarding.contract_draft_sent_at && (
                <p className="text-xs text-slate-400 mt-3">
                  계약서 초안 발송: {new Date(onboarding.contract_draft_sent_at).toLocaleString('ko-KR')}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* 파일 미리보기 모달 */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={closePreview}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">{previewLabel}</h3>
              <button onClick={closePreview} className="text-slate-400 hover:text-slate-600 text-lg font-bold px-2">
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 min-h-0">
              {previewType === 'pdf' ? (
                <iframe src={`${previewUrl}#toolbar=1`} className="w-full h-[75vh] border-0 rounded-lg" title={previewLabel} />
              ) : (
                <img src={previewUrl} alt={previewLabel} className="max-w-full max-h-[75vh] mx-auto rounded-lg" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* 입사일 안내 발송 */}
      {(() => {
        const guideSentLog = onboarding?.orientation_sent_log?.filter(
          (log: any) => log.type === 'onboarding-guide'
        ) || [];
        const isSent = guideSentLog.length > 0;
        const lastSent = isSent ? guideSentLog[guideSentLog.length - 1] : null;

        return (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-700">입사일 안내</h2>
              {isSent && (
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                  발송됨
                </span>
              )}
            </div>

            <div className={`flex items-center justify-between p-4 rounded-lg ${
              isSent ? 'bg-green-50 border border-green-200' : 'bg-indigo-50 border border-indigo-200'
            }`}>
              <div>
                <p className={`text-sm font-medium ${isSent ? 'text-green-800' : 'text-indigo-800'}`}>
                  입사 후 수행 내용 안내 메일
                </p>
                <p className={`text-xs mt-0.5 ${isSent ? 'text-green-600' : 'text-indigo-600'}`}>
                  {isSent
                    ? `최근 발송: ${new Date(lastSent.sent_at).toLocaleString('ko-KR')} (${lastSent.to})`
                    : '입사일에 자동 발송됩니다. 수동으로 먼저 발송할 수도 있습니다.'
                  }
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  과학기술인번호, 이메일 서명, 회사소개서/준수사항 열람 링크 포함
                </p>
              </div>
              <button
                onClick={async () => {
                  const ok = await confirm({
                    title: isSent ? '입사일 안내 재발송' : '입사일 안내 발송',
                    message: `${applicant.name}님에게 입사일 안내 이메일을 ${isSent ? '재' : ''}발송하시겠습니까?`,
                    confirmText: isSent ? '재발송' : '발송',
                  });
                  if (!ok) return;
                  try {
                    const result = await adminApi.sendOnboardingGuide(applicant.id);
                    toast.success(result.message || '발송 완료');
                    // 데이터 리로드
                    const res = await adminApi.getApplicantDetail(applicant.id);
                    if (res.onboarding) setOnboarding(res.onboarding);
                  } catch (err: any) {
                    toast.error(err.response?.data?.message || '발송 실패');
                  }
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  isSent
                    ? 'bg-white border border-green-300 text-green-700 hover:bg-green-50'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {isSent ? '재발송' : '발송'}
              </button>
            </div>
          </div>
        );
      })()}

      {/* workspace 직원 등록 */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">직원 등록</h2>
        {wsStatus?.registered ? (
          wsStatus.deleted ? (
            <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div>
                <p className="text-sm font-medium text-orange-800">workspace에서 삭제된 직원입니다</p>
                <p className="text-xs text-orange-600 mt-0.5">사번: {wsStatus.empNo}</p>
              </div>
              <button onClick={handleReRegisterWorkspace} disabled={wsLoading}
                className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">
                {wsLoading ? '처리 중...' : '재등록'}
              </button>
            </div>
          ) : (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">workspace에 등록됨</p>
                  <p className="text-xs text-green-600 mt-0.5">사번: {wsStatus.empNo} / {wsStatus.email}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleOpenSync} disabled={wsLoading}
                    className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                    {wsLoading ? '확인 중...' : '동기화'}
                  </button>
                  <button onClick={handleRemoveWorkspace} disabled={wsLoading}
                    className="px-4 py-2 text-sm font-medium bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50">
                    {wsLoading ? '처리 중...' : '삭제'}
                  </button>
                </div>
              </div>

              {/* 동기화 상태 패널 */}
              {showSyncModal && syncStatus && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">동기화 현황</h3>

                  {/* 서류 동기화 */}
                  <div className="mb-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">입사서류</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {syncStatus.documents.map((doc: any) => (
                        <div key={doc.key} className={`flex items-center gap-1.5 text-xs px-2 py-1.5 rounded ${
                          !doc.hireUploaded ? 'bg-slate-100 text-slate-400' :
                          doc.workspaceSynced ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          <span>{doc.workspaceSynced ? '✓' : doc.hireUploaded ? '○' : '—'}</span>
                          <span>{doc.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 데이터 동기화 */}
                  <div className="mb-3">
                    <p className="text-xs font-medium text-slate-500 mb-2">자동 추출 데이터</p>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center justify-between px-2 py-1.5 rounded bg-slate-50">
                        <span className="text-slate-600">프로필 사진</span>
                        <span className={syncStatus.data.profilePhoto.synced ? 'text-green-600 font-medium' : 'text-slate-400'}>
                          {syncStatus.data.profilePhoto.synced ? '✓ 동기화됨' : '미동기화'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between px-2 py-1.5 rounded bg-slate-50">
                        <span className="text-slate-600">은행/계좌</span>
                        <span className={syncStatus.data.bankName.synced ? 'text-green-600 font-medium' : 'text-slate-400'}>
                          {syncStatus.data.bankName.synced ? `✓ ${syncStatus.data.bankName.workspace}` :
                           syncStatus.data.bankName.hire ? `○ OCR: ${syncStatus.data.bankName.hire}` : '미추출'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between px-2 py-1.5 rounded bg-slate-50">
                        <span className="text-slate-600">주민번호</span>
                        <span className={syncStatus.data.ssn.synced ? 'text-green-600 font-medium' : 'text-slate-400'}>
                          {syncStatus.data.ssn.synced ? '✓ 동기화됨' :
                           syncStatus.data.ssn.hire ? '○ OCR 추출됨' : '미추출'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 동기화 버튼 */}
                  <div className="flex items-center justify-between pt-2">
                    <button onClick={() => setShowSyncModal(false)}
                      className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700">
                      닫기
                    </button>
                    <button onClick={handleSync} disabled={syncLoading}
                      className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                      {syncLoading ? '동기화 중...' : '동기화 실행'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-sm font-medium text-slate-700">workspace에 미등록</p>
            <p className="text-xs text-slate-500 mt-0.5">
              위 <span className="font-medium">‘서류 승인 및 직원 등록’</span>으로 등록하세요. 사번·회사 이메일과 함께 Google·MS365 계정이 자동 생성됩니다.
            </p>
          </div>
        )}
      </div>

      {/* 명함 신청 */}
      <BusinessCardSection applicant={applicant} toast={toast} />
    </div>
  );
};

const BusinessCardSection: React.FC<{ applicant: any; toast: any }> = ({ applicant, toast }) => {
  const [form, setForm] = useState({
    nameKr: applicant.name || '',
    nameEn: '',
    titleKr: '',
    titleEn: '',
    email: '',
    mobile: applicant.phone || '',
  });
  const [loading, setLoading] = useState(false);
  const [titleSource, setTitleSource] = useState<string | null>(null);

  useEffect(() => {
    // 두 호출을 따로 setForm 하면 늦게 도착한 쪽이 상대를 덮어쓴다.
    // 특히 영문 이름은 승인 시 확정값(name_en)이 로마자 자동변환보다 우선해야 하므로
    // 둘을 모두 받은 뒤 한 번에 반영한다.
    let cancelled = false;
    Promise.all([
      adminApi.getApplicantDetail(applicant.id).catch(() => null),
      adminApi.suggestEmail(applicant.name).catch(() => null),
    ]).then(([detail, suggested]: any[]) => {
      if (cancelled) return;
      const d = detail?.businessCardDefaults;
      const romanized = suggested
        ? `${suggested.firstName || ''} ${suggested.lastName || ''}`.trim()
        : '';
      setForm(f => ({
        ...f,
        // 승인 화면에서 확정한 철자 → 없으면 로마자 자동변환
        nameEn: d?.nameEn || romanized || f.nameEn,
        titleKr: d?.titleKr || f.titleKr,
        titleEn: d?.titleEn || f.titleEn,
        email: d?.email || f.email,
        mobile: d?.mobile || f.mobile,
      }));
      if (d) setTitleSource(d.titleSource);
    });
    return () => { cancelled = true; };
  }, [applicant.id, applicant.name]);

  const handleChange = (field: string, value: string) =>
    setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async () => {
    if (!form.nameKr || !form.email || !form.mobile || !form.titleKr || !form.titleEn) {
      toast.error('이름(한글), 직급(한글/영문), 이메일, 전화번호는 필수입니다.');
      return;
    }
    setLoading(true);
    try {
      await adminApi.requestBusinessCard(applicant.id, form);
      toast.success('명함 PDF가 생성되어 이메일로 발송되었습니다.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || '명함 생성에 실패했습니다.');
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h2 className="text-sm font-semibold text-slate-700 mb-4">명함 신청</h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">이름 (한글)</label>
          <input value={form.nameKr} onChange={e => handleChange('nameKr', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="홍길동" />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">이름 (영문)</label>
          <input value={form.nameEn} onChange={e => handleChange('nameEn', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Gildong Hong" />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">
            직급 (한글)
            {titleSource === 'fallback' && (
              <span className="ml-1 text-amber-600">· 오퍼에서 직책을 못 찾아 기본값입니다. 확인하세요</span>
            )}
          </label>
          <input value={form.titleKr} onChange={e => handleChange('titleKr', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="이사" />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">직급 (영문)</label>
          <input value={form.titleEn} onChange={e => handleChange('titleEn', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Director" />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">이메일</label>
          <input value={form.email} onChange={e => handleChange('email', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="hong@grkcon.com" />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">전화번호</label>
          <input value={form.mobile} onChange={e => handleChange('mobile', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="01012345678" />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button onClick={handleSubmit} disabled={loading}
          className="px-5 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
          {loading ? '생성 중...' : '명함 PDF 생성 및 발송'}
        </button>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h2 className="text-sm font-semibold text-slate-700 mb-4">{title}</h2>
    {children}
  </div>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-400 mb-0.5">{label}</p>
    <p className="text-sm text-slate-700">{value}</p>
  </div>
);

const ScoreCard: React.FC<{ label: string; score: number }> = ({ label, score }) => (
  <div className="text-center p-3 bg-slate-50 rounded-lg">
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className={`text-lg font-semibold ${score >= 70 ? 'text-green-600' : score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{score}</p>
  </div>
);

// ========== 오퍼 관리 섹션 ==========

const offerStatusLabels: Record<string, { label: string; color: string }> = {
  PENDING: { label: '저장됨', color: 'bg-amber-100 text-amber-800' },
  SENT: { label: '발송됨', color: 'bg-blue-100 text-blue-800' },
  ACCEPTED: { label: '수락됨', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: '거절됨', color: 'bg-red-100 text-red-800' },
  NEGOTIATING: { label: '재조정중', color: 'bg-orange-100 text-orange-800' },
};

const OfferSection: React.FC<{
  applicant: any;
  offer: any;
  campaign: any;
  onUpdate: () => void;
}> = ({ applicant, offer, campaign, onUpdate }) => {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    positionTitle: '',
    positionDuties: '',
    baseSalary: '',
    bonus: '',
    benefits: '',
    totalSalary: '',
    startDate: '',
    endDate: '',
    workLocation: '서울특별시 용산구 이태원로 200, 511호',

    employmentType: '정규직',
    compensationDetail: '',
    roleDescription: '',
    referenceCheckRequested: false,
  });

  const fillForm = (o: any) => {
    setForm({
      positionTitle: o.position_title || '',
      positionDuties: o.position_duties || '',
      baseSalary: o.base_salary ? String(Math.round(Number(o.base_salary) / 10000)) : '',
      bonus: o.bonus ? String(Math.round(Number(o.bonus) / 10000)) : '',
      benefits: o.benefits ? String(Math.round(Number(o.benefits) / 10000)) : '',
      totalSalary: o.total_salary ? String(Math.round(Number(o.total_salary) / 10000)) : '',
      startDate: o.start_date || '',
      endDate: o.end_date || '',
      workLocation: o.work_location || '',
      employmentType: o.employment_type || '정규직',
      compensationDetail: o.compensation_detail || '',
      roleDescription: o.role_description || '',
      referenceCheckRequested: o.reference_check_requested || false,
    });
  };

  const handleSave = async (sendEmail: boolean) => {
    if (!form.positionTitle.trim()) {
      toast.error('포지션을 입력해주세요.');
      return;
    }
    if (!form.baseSalary) {
      toast.error('기본급을 입력해주세요.');
      return;
    }
    // 총액 자동 계산
    const autoTotal = Number(form.baseSalary || 0) + Number(form.bonus || 0) + Number(form.benefits || 0);
    // compensationDetail: 추가 처우 조건만 저장 (급여 부분은 BE에서 숫자 필드로 동적 생성)
    const autoCompDetail = form.employmentType === '인턴'
      ? ''
      : [
          '성과급 : 연봉의 최대 50%내에서 연 1회 Profit share 별도지급',
          '퇴직금 별도 지급',
          '명절(설, 추석) 생일 선물 및 경조사비 별도 지원',
          '학습 및 서적구매 비용 지원',
        ].join('\n');
    setSubmitting(true);
    try {
      await adminApi.createOffer({
        applicant_id: applicant.id,
        position_title: form.positionTitle,
        position_duties: form.positionDuties || undefined,
        base_salary: form.baseSalary ? parseInt(form.baseSalary) * 10000 : undefined,
        bonus: form.bonus ? parseInt(form.bonus) * 10000 : undefined,
        benefits: form.benefits ? parseInt(form.benefits) * 10000 : undefined,
        total_salary: autoTotal * 10000,
        start_date: form.startDate || undefined,
        end_date: form.endDate || undefined,
        work_location: form.workLocation || undefined,
        send_email: sendEmail,
        employment_type: form.employmentType || undefined,
        compensation_detail: autoCompDetail,
        role_description: form.roleDescription || undefined,
        reference_check_requested: form.referenceCheckRequested,
      });
      toast.success(sendEmail ? '오퍼가 저장되고 이메일이 발송되었습니다.' : '오퍼가 저장되었습니다.');
      setShowForm(false);
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '오퍼 저장 실패');
    }
    setSubmitting(false);
  };

  const handleSend = async () => {
    const ok = await confirm({
      title: '오퍼 이메일을 발송하시겠습니까?',
      confirmText: '발송',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await adminApi.sendOffer(offer.id);
      toast.success('오퍼 이메일이 발송되었습니다.');
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '발송 실패');
    }
    setSubmitting(false);
  };

  const formatSalary = (val: string | number | null | undefined) =>
    val ? Math.round(Number(val) / 10000).toLocaleString('ko-KR') + '만원' : '-';

  // 폼 UI
  if (showForm) {
    return (
      <Section title="오퍼">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">포지션 *</label>
              <input type="text" value={form.positionTitle}
                onChange={(e) => setForm({ ...form, positionTitle: e.target.value })}
                placeholder="예: Business Analyst"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">고용형태</label>
              <select value={form.employmentType}
                onChange={(e) => {
                  const type = e.target.value;
                  if (type === '인턴') {
                    setForm({
                      ...form,
                      employmentType: type,
                      positionTitle: 'Research Assistant',
                      roleDescription: '의료/헬스케어 비즈니스 전략컨설턴트',
                      positionDuties: '- 의료 및 디지털 헬스케어 컨설팅 및 연구 관련 데이터 분석지원 업무\n- 의료 및 디지털 헬스케어 기업에 대한 투자업무 지원 업무\n- 상기 업무 수행을 위한 사내 인턴 교육프로그램 및 현장업무 트레이닝 코스 제공',
                      bonus: '',
                      benefits: '',
                      totalSalary: '',
                    });
                  } else {
                    setForm({
                      ...form,
                      employmentType: type,
                      positionTitle: '',
                      roleDescription: '디지털헬스케어 연구 수행 및 전략컨설팅',
                      positionDuties: '- 의료 및 디지털 헬스케어 연구 관련 데이터 분석 및 컨설팅\n- 의료, 헬스케어 관련 연구 및 대외 컨퍼런스의 강연 활동 등의 수행\n- 의료 및 디지털 헬스케어 관련 컨설팅 수행 및 지원',
                    });
                  }
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="정규직">정규직</option>
                <option value="계약직">계약직</option>
                <option value="인턴">인턴</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">역할 설명 (Offer Letter 인사 문구)</label>
            <input type="text" value={form.roleDescription}
              onChange={(e) => setForm({ ...form, roleDescription: e.target.value })}
              placeholder="예: IT admin 및 디지털헬스케어 전략컨설팅"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">직무 설명 (Offer Letter 업무 항목)</label>
            <textarea value={form.positionDuties}
              onChange={(e) => setForm({ ...form, positionDuties: e.target.value })}
              placeholder="줄바꿈으로 구분&#10;- 연구 아웃소싱 플랫폼 구축&#10;- 의료 헬스케어 컨설팅 지원"
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
          </div>
          {applicant.requested_salary && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
              <span className="text-xs font-medium text-amber-700">지원자 희망 연봉:</span>
              <span className="text-sm font-bold text-amber-800">{Number(applicant.requested_salary).toLocaleString('ko-KR')}만원</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">기본급 (만원) *</label>
              <input type="number" value={form.baseSalary}
                onChange={(e) => setForm({ ...form, baseSalary: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
            </div>
            {form.employmentType !== '인턴' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">상여금 (만원)</label>
              <input type="number" value={form.bonus}
                onChange={(e) => setForm({ ...form, bonus: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
            </div>
            )}
            {form.employmentType !== '인턴' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">복리후생 (만원)</label>
              <input type="number" value={form.benefits}
                onChange={(e) => setForm({ ...form, benefits: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
            </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">급여/복리후생 상세 (Offer Letter PDF 3번 항목)</label>
            <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg leading-relaxed whitespace-pre-wrap">
              {form.employmentType === '인턴' ? (
                <>- 기본급 : 연 {form.baseSalary ? Number(form.baseSalary).toLocaleString('ko-KR') : '___'}만원{'\n'}
                  {'  '}※ 인턴십 기한 내 급여를 연봉으로 환산한 금액이며, 인턴십 기간 내에{'\n'}
                  {'  '}해당 금액을 지급한다는 것을 의미하지 않으며, 정규직 채용을 의미하지 않음{'\n'}
                  - 인턴십 기간 내 명절(설, 추석) 선물 별도 지원{'\n'}
                  - (필요 시) 학습 및 서적구매 비용 지원</>
              ) : (
                <>Annual Payment Base and Compensations : 총 {((Number(form.baseSalary || 0) + Number(form.bonus || 0) + Number(form.benefits || 0)) || 0).toLocaleString('ko-KR')}만원{'\n'}
                  - 기본급 : 연 {form.baseSalary ? Number(form.baseSalary).toLocaleString('ko-KR') : '___'}만원{'\n'}
                  {form.bonus ? `- 상여금 : ${Number(form.bonus).toLocaleString('ko-KR')}만원\n` : ''}
                  {form.benefits ? `- 현금성 복지 : 연 ${Number(form.benefits).toLocaleString('ko-KR')}만원\n` : ''}
                  - 성과급 : 연봉의 최대 50%내에서 연 1회 Profit share 별도지급{'\n'}
                  - 퇴직금 별도 지급{'\n'}
                  - 명절(설, 추석) 생일 선물 및 경조사비 별도 지원{'\n'}
                  - 학습 및 서적구매 비용 지원</>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">입사 예정일</label>
              <input type="date" value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            {(form.employmentType === '계약직' || form.employmentType === '인턴') && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">계약 종료일</label>
                <input type="date" value={form.endDate || ''}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">근무지</label>
              <input type="text" value={form.workLocation}
                onChange={(e) => setForm({ ...form, workLocation: e.target.value })}
                placeholder="예: 서울 강남구"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setShowForm(false)}
              className="px-3 py-2 text-xs border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50">
              취소
            </button>
            <button onClick={async () => {
              if (!form.baseSalary) { toast.error('기본급을 입력해주세요.'); return; }
              setSubmitting(true);
              try {
                const autoComp = form.employmentType === '인턴'
                  ? `기본급 : 연 ${Number(form.baseSalary).toLocaleString('ko-KR')}만원`
                  : [
                      `기본급 : 연 ${Number(form.baseSalary).toLocaleString('ko-KR')}만원`,
                      form.bonus ? `상여금 : ${Number(form.bonus).toLocaleString('ko-KR')}만원` : '',
                      form.benefits ? `현금성 복지 : 연 ${Number(form.benefits).toLocaleString('ko-KR')}만원` : '',
                      '성과급 : 연봉의 최대 50%내에서 연 1회 Profit share 별도지급',
                      '퇴직금 별도 지급',
                      '명절(설, 추석) 생일 선물 및 경조사비 별도 지원',
                      '학습 및 서적구매 비용 지원',
                    ].filter(Boolean).join('\n');
                const blob = await adminApi.previewOfferPdf({
                  applicant_name: applicant.name,
                  employment_type: form.employmentType,
                  role_description: form.roleDescription,
                  position_title: form.positionTitle,
                  position_duties: form.positionDuties,
                  compensation_detail: autoComp,
                  start_date: form.startDate,
                  base_salary: form.baseSalary ? parseInt(form.baseSalary) * 10000 : 0,
                });
                const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                window.open(url, '_blank');
              } catch { toast.error('PDF 미리보기 실패'); }
              setSubmitting(false);
            }} disabled={submitting}
              className="px-3 py-2 text-xs border border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 disabled:opacity-50">
              {submitting ? 'PDF 생성 중...' : 'PDF 미리보기'}
            </button>
            <button onClick={() => handleSave(false)} disabled={submitting}
              className="flex-1 px-3 py-2 text-xs bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50">
              {submitting ? '저장 중...' : '저장'}
            </button>
            <button onClick={() => handleSave(true)} disabled={submitting}
              className="flex-1 px-3 py-2 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {submitting ? '발송 중...' : '발송'}
            </button>
          </div>
          <div className="mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.referenceCheckRequested}
                onChange={(e) => setForm(f => ({ ...f, referenceCheckRequested: e.target.checked }))}
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <span className="text-xs text-slate-600">📋 평판조회 동의서 발송 (오퍼 이메일에 안내 포함)</span>
            </label>
          </div>
        </div>
      </Section>
    );
  }

  // 오퍼가 없는 경우
  if (!offer) {
    return (
      <Section title="오퍼">
        <div className="text-center py-4">
          <p className="text-sm text-slate-400 mb-3">오퍼 내역이 없습니다.</p>
          <button onClick={() => { setShowForm(true); setForm({ positionTitle: '', positionDuties: '- 의료 및 디지털 헬스케어 연구 관련 데이터 분석 및 컨설팅\n- 의료, 헬스케어 관련 연구 및 대외 컨퍼런스의 강연 활동 등의 수행\n- 의료 및 디지털 헬스케어 관련 컨설팅 수행 및 지원', baseSalary: '', bonus: '', benefits: '', totalSalary: '', startDate: '', endDate: '', workLocation: '서울특별시 용산구 이태원로 200, 511호', employmentType: '정규직', compensationDetail: '', roleDescription: '디지털헬스케어 연구 수행 및 전략컨설팅', referenceCheckRequested: false }); }}
            className="px-4 py-2 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 font-medium">
            오퍼 생성
          </button>
        </div>
      </Section>
    );
  }

  // 오퍼가 있는 경우: 상태별 표시
  const statusInfo = offerStatusLabels[offer.status] || { label: offer.status, color: 'bg-slate-100 text-slate-600' };

  return (
    <Section title="오퍼">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className={`text-xs px-2 py-1 rounded font-medium ${statusInfo.color}`}>{statusInfo.label}</span>
          <div className="flex gap-2">
            {offer.status !== 'ACCEPTED' && (
              <>
                <button onClick={() => { fillForm(offer); setShowForm(true); }}
                  className="px-3 py-1 text-xs border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50">
                  수정
                </button>
                <button onClick={handleSend} disabled={submitting}
                  className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {submitting ? '발송 중...' : offer.status === 'SENT' ? '재발송' : '발송'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="포지션" value={offer.position_title || '-'} />
          <InfoRow label="근무지" value={offer.work_location || '-'} />
          <InfoRow label="기본급" value={formatSalary(offer.base_salary)} />
          <InfoRow label="상여금" value={formatSalary(offer.bonus)} />
          <InfoRow label="복리후생" value={formatSalary(offer.benefits)} />
          <InfoRow label="연봉 총액" value={formatSalary(offer.total_salary)} />
          <InfoRow label="입사 예정일" value={
            `${offer.start_date || '-'} ${offer.start_date_confirmed ? '✅ 확인됨' : '⏳ 미확인'}`
          } />
          <InfoRow label="상태" value={statusInfo.label} />
        </div>

        {offer.position_duties && (
          <div>
            <p className="text-xs text-slate-400 mb-1">직무 설명</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">{offer.position_duties}</p>
          </div>
        )}

        {offer.sent_at && (
          <p className="text-xs text-slate-400">발송: {new Date(offer.sent_at).toLocaleString('ko-KR')}</p>
        )}
        {offer.accepted_at && (
          <p className="text-xs text-green-600">수락: {new Date(offer.accepted_at).toLocaleString('ko-KR')}</p>
        )}

      </div>
    </Section>
  );
};

// ========== 면접 관리 섹션 ==========

const TIME_OPTIONS = Array.from({ length: 23 }, (_, i) => {
  const hour = Math.floor(i / 2) + 9;
  const min = i % 2 === 0 ? '00' : '30';
  return `${String(hour).padStart(2, '0')}:${min}`;
});

const interviewStatusMap: Record<string, { label: string; color: string }> = {
  PROPOSAL_SENT: { label: '일정 제안됨', color: 'bg-blue-100 text-blue-800' },
  APPLICANT_SELECTED: { label: '지원자 선택 완료', color: 'bg-cyan-100 text-cyan-800' },
  ALTERNATIVE_PROPOSED: { label: '대안 일정 제시', color: 'bg-amber-100 text-amber-800' },
  CONFIRMED: { label: '확정', color: 'bg-green-100 text-green-800' },
  COMPLETED: { label: '완료', color: 'bg-indigo-100 text-indigo-800' },
  CANCELLED: { label: '취소', color: 'bg-red-100 text-red-800' },
};

const formatInterviewDate = (dateStr: string) => {
  const date = new Date(dateStr + 'T00:00:00');
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} (${days[date.getDay()]})`;
};

const InterviewSection: React.FC<{
  applicant: any;
  interview: any;
  onUpdate: () => void;
}> = ({ applicant, interview, onUpdate }) => {
  const { toast } = useToast();
  const confirm = useConfirm();
  const [interviewers, setInterviewers] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // 일정 제안 폼
  const [showPropose, setShowPropose] = useState(false);
  const [proposeDates, setProposeDates] = useState([{ date: '', time: '' }]);
  const [proposeMessage, setProposeMessage] = useState('');

  // 확정 폼
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInterviewerId, setConfirmInterviewerId] = useState<number | null>(null);
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmTime, setConfirmTime] = useState('');

  // 재제안 폼
  const [showRepropose, setShowRepropose] = useState(false);
  const [reproposeDates, setReproposeDates] = useState([{ date: '', time: '' }]);
  const [reproposeMessage, setReproposeMessage] = useState('');

  // 평가 폼
  const [showEvaluate, setShowEvaluate] = useState(false);
  const [evalScore, setEvalScore] = useState(0);
  const [evalNotes, setEvalNotes] = useState('');

  useEffect(() => {
    interviewAdminApi.getInterviewers().then(setInterviewers).catch(() => {});
  }, []);

  const resetForms = () => {
    setShowPropose(false);
    setShowConfirm(false);
    setShowRepropose(false);
    setShowEvaluate(false);
    setProposeDates([{ date: '', time: '' }]);
    setProposeMessage('');
    setReproposeDates([{ date: '', time: '' }]);
    setReproposeMessage('');
  };

  const handlePropose = async () => {
    const validDates = proposeDates.filter((d) => d.date && d.time);
    if (validDates.length === 0) { toast.error('최소 1개의 일정을 입력해주세요.'); return; }
    setSubmitting(true);
    try {
      await interviewAdminApi.propose(applicant.id, validDates, proposeMessage || undefined);
      toast.success('면접 일정이 제안되었습니다.');
      resetForms();
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '제안 실패');
    }
    setSubmitting(false);
  };

  const handleConfirm = async () => {
    if (!confirmInterviewerId || !confirmDate || !confirmTime) {
      toast.error('면접관, 날짜, 시간을 모두 선택해주세요.'); return;
    }
    setSubmitting(true);
    try {
      await interviewAdminApi.confirmSchedule(interview.id, confirmInterviewerId, confirmDate, confirmTime);
      toast.success('면접이 확정되었습니다. Zoom 링크가 자동 생성됩니다.');
      resetForms();
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '확정 실패');
    }
    setSubmitting(false);
  };

  const handleRepropose = async () => {
    const validDates = reproposeDates.filter((d) => d.date && d.time);
    if (validDates.length === 0) { toast.error('최소 1개의 일정을 입력해주세요.'); return; }
    setSubmitting(true);
    try {
      await interviewAdminApi.repropose(interview.id, validDates, reproposeMessage || undefined);
      toast.success('일정이 재제안되었습니다.');
      resetForms();
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '재제안 실패');
    }
    setSubmitting(false);
  };

  const handleEvaluate = async (passed: boolean) => {
    setSubmitting(true);
    try {
      await interviewAdminApi.evaluateInterview(interview.id, evalScore, evalNotes, passed);
      toast.success(passed ? '면접 합격 처리되었습니다.' : '면접 불합격 처리되었습니다.');
      resetForms();
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '평가 실패');
    }
    setSubmitting(false);
  };

  const handleCancel = async () => {
    const ok = await confirm({
      title: '이 면접을 취소하시겠습니까?',
      confirmText: '취소하기',
      variant: 'danger',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await interviewAdminApi.cancelInterview(interview.id);
      toast.success('면접이 취소되었습니다.');
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '취소 실패');
    }
    setSubmitting(false);
  };

  const updateDateRow = (
    setter: React.Dispatch<React.SetStateAction<{ date: string; time: string }[]>>,
    current: { date: string; time: string }[],
    idx: number, field: 'date' | 'time', value: string,
  ) => {
    const updated = [...current];
    updated[idx] = { ...updated[idx], [field]: value };
    setter(updated);
  };

  const DateRows: React.FC<{
    dates: { date: string; time: string }[];
    setDates: React.Dispatch<React.SetStateAction<{ date: string; time: string }[]>>;
  }> = ({ dates, setDates }) => (
    <div className="space-y-2">
      {dates.map((d, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <input type="date" value={d.date}
            onChange={(e) => updateDateRow(setDates, dates, idx, 'date', e.target.value)}
            className="flex-1 px-2 py-1.5 border rounded-lg text-xs" />
          <select value={d.time}
            onChange={(e) => updateDateRow(setDates, dates, idx, 'time', e.target.value)}
            className="px-2 py-1.5 border rounded-lg text-xs">
            <option value="">시간</option>
            {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {dates.length > 1 && (
            <button onClick={() => setDates(dates.filter((_, i) => i !== idx))}
              className="text-red-400 hover:text-red-600 text-xs">X</button>
          )}
        </div>
      ))}
      {dates.length < 3 && (
        <button onClick={() => setDates([...dates, { date: '', time: '' }])}
          className="text-xs text-indigo-600 hover:text-indigo-700">+ 일정 추가 (최대 3개)</button>
      )}
    </div>
  );

  // 면접 없음 → 일정 제안하기
  if (!interview) {
    return (
      <Section title="면접">
        {showPropose ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">제안 일정</label>
              <DateRows dates={proposeDates} setDates={setProposeDates} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">메시지 (선택)</label>
              <textarea value={proposeMessage} onChange={(e) => setProposeMessage(e.target.value)}
                rows={2} placeholder="지원자에게 전달할 메시지..."
                className="w-full px-2 py-1.5 border rounded-lg text-xs resize-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => { resetForms(); }}
                className="flex-1 px-3 py-1.5 text-xs border rounded-lg text-slate-600 hover:bg-slate-50">취소</button>
              <button onClick={handlePropose} disabled={submitting}
                className="flex-1 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {submitting ? '제안 중...' : '일정 제안하기'}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-slate-400 mb-3">면접 내역이 없습니다.</p>
            {applicant.status === 'INTERVIEW' && (
              <div className="flex gap-2 justify-center">
                <button onClick={() => setShowPropose(true)}
                  className="px-4 py-2 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 font-medium">
                  일정 제안하기
                </button>
                <button onClick={async () => {
                  const ok = await confirm({ title: '기한경과 불합격 처리하시겠습니까?', confirmText: '불합격', variant: 'danger' });
                  if (!ok) return;
                  try {
                    await adminApi.updateStatus(applicant.id, 'REJECTED', 'EXPIRED');
                    toast.success('기한경과 불합격 처리되었습니다.');
                    onUpdate();
                  } catch (err: any) { toast.error(err.response?.data?.message || '처리 실패'); }
                }}
                  className="px-4 py-2 bg-orange-50 text-orange-600 text-xs rounded-lg hover:bg-orange-100 font-medium border border-orange-200">
                  기한경과 불합격
                </button>
              </div>
            )}
          </div>
        )}
      </Section>
    );
  }

  const status = interviewStatusMap[interview.status] || { label: interview.status, color: 'bg-slate-100 text-slate-600' };

  return (
    <Section title="면접">
      <div className="space-y-4">
        {/* 상태 + 액션 버튼 */}
        <div className="flex items-center justify-between">
          <span className={`text-xs px-2 py-1 rounded font-medium ${status.color}`}>{status.label}</span>
          <div className="flex gap-2">
            {interview.status === 'APPLICANT_SELECTED' && (
              <button onClick={() => {
                setConfirmDate(interview.scheduled_date || '');
                setConfirmTime(interview.scheduled_time || '');
                setShowConfirm(true);
              }} className="px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700">
                확정하기
              </button>
            )}
            {interview.status === 'ALTERNATIVE_PROPOSED' && (
              <>
                <button onClick={() => {
                  const lastApplicantProposal = [...(interview.proposals || [])].reverse().find((p: any) => p.proposed_by === 'APPLICANT');
                  if (lastApplicantProposal?.proposed_dates?.length > 0) {
                    setConfirmDate(lastApplicantProposal.proposed_dates[0].date);
                    setConfirmTime(lastApplicantProposal.proposed_dates[0].time);
                  }
                  setShowConfirm(true);
                }} className="px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700">
                  수락/확정
                </button>
                <button onClick={() => setShowRepropose(true)}
                  className="px-3 py-1 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700">
                  재제안
                </button>
              </>
            )}
            {interview.status === 'CONFIRMED' && (
              <button onClick={() => {
                setEvalScore(interview.score || 0);
                setEvalNotes(interview.notes || '');
                setShowEvaluate(true);
              }} className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                평가하기
              </button>
            )}
            {!['COMPLETED', 'CANCELLED'].includes(interview.status) && (
              <button onClick={handleCancel} disabled={submitting}
                className="px-3 py-1 text-xs border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50">
                취소
              </button>
            )}
            {applicant.status === 'INTERVIEW' && (
              <button onClick={async () => {
                const ok = await confirm({ title: '기한경과 불합격 처리하시겠습니까?', confirmText: '불합격', variant: 'danger' });
                if (!ok) return;
                try {
                  await adminApi.updateStatus(applicant.id, 'REJECTED', 'EXPIRED');
                  toast.success('기한경과 불합격 처리되었습니다.');
                  onUpdate();
                } catch (err: any) { toast.error(err.response?.data?.message || '처리 실패'); }
              }}
                className="px-3 py-1 text-xs bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 border border-orange-200">
                기한경과 불합격
              </button>
            )}
          </div>
        </div>

        {/* 확정 정보 */}
        {interview.status === 'CONFIRMED' && (
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-green-600">확정 일정</span>
                <p className="font-medium text-green-800">
                  {interview.scheduled_date ? formatInterviewDate(interview.scheduled_date) : '-'} {interview.scheduled_time}
                </p>
              </div>
              {interview.interviewer && (
                <div>
                  <span className="text-green-600">면접관</span>
                  <p className="font-medium text-green-800">{interview.interviewer.name} ({interview.interviewer.position})</p>
                </div>
              )}
            </div>
            {interview.zoom_url && (
              <div className="mt-2 pt-2 border-t border-green-200">
                <span className="text-xs text-green-600">Zoom 링크</span>
                <a href={interview.zoom_url} target="_blank" rel="noopener noreferrer"
                  className="block text-xs text-blue-600 hover:text-blue-700 truncate mt-0.5">{interview.zoom_url}</a>
              </div>
            )}
          </div>
        )}

        {/* 완료 정보 */}
        {interview.status === 'COMPLETED' && (
          <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-indigo-600">면접 일정</span>
                <p className="font-medium text-indigo-800">
                  {interview.scheduled_date ? formatInterviewDate(interview.scheduled_date) : '-'} {interview.scheduled_time}
                </p>
              </div>
              <div>
                <span className="text-indigo-600">점수</span>
                <p className="font-medium text-indigo-800">{interview.score !== null ? `${interview.score}점` : '-'}</p>
              </div>
              {interview.interviewer && (
                <div>
                  <span className="text-indigo-600">면접관</span>
                  <p className="font-medium text-indigo-800">{interview.interviewer.name}</p>
                </div>
              )}
            </div>
            {interview.notes && (
              <div className="mt-2 pt-2 border-t border-indigo-200">
                <span className="text-xs text-indigo-600">메모</span>
                <p className="text-xs text-indigo-800 mt-0.5">{interview.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* 선택된 일정 표시 (APPLICANT_SELECTED) */}
        {interview.status === 'APPLICANT_SELECTED' && interview.scheduled_date && (
          <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 text-xs">
            <span className="text-cyan-600">지원자 선택 일정</span>
            <p className="font-medium text-cyan-800 mt-0.5">
              {formatInterviewDate(interview.scheduled_date)} {interview.scheduled_time}
            </p>
          </div>
        )}

        {/* 협의 히스토리 */}
        {interview.proposals && interview.proposals.length > 0 && (
          <div className="pt-3 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 mb-2">일정 협의 히스토리</p>
            <div className="space-y-2">
              {interview.proposals
                .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                .map((p: any) => (
                <div key={p.id} className={`p-2 rounded text-xs ${
                  p.proposed_by === 'ADMIN' ? 'bg-indigo-50' : 'bg-emerald-50'
                }`}>
                  <span className={`font-medium ${p.proposed_by === 'ADMIN' ? 'text-indigo-700' : 'text-emerald-700'}`}>
                    {p.proposed_by === 'ADMIN' ? '관리자' : '지원자'}
                  </span>
                  <span className="text-slate-400 ml-2">{new Date(p.created_at).toLocaleString('ko-KR')}</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.proposed_dates.map((d: any, i: number) => (
                      <span key={i} className={`px-1.5 py-0.5 rounded ${
                        p.selected_index === i ? 'bg-indigo-200 text-indigo-800 font-medium' : 'bg-white text-slate-600'
                      }`}>
                        {d.date} {d.time}{p.selected_index === i ? ' (선택)' : ''}
                      </span>
                    ))}
                  </div>
                  {p.message && <p className="text-slate-500 mt-1">{p.message}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 확정 폼 */}
        {showConfirm && (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <p className="text-xs font-semibold text-slate-700">면접 확정</p>
            <div>
              <label className="block text-xs text-slate-600 mb-1">면접관</label>
              <select value={confirmInterviewerId || ''}
                onChange={(e) => setConfirmInterviewerId(parseInt(e.target.value) || null)}
                className="w-full px-2 py-1.5 border rounded-lg text-xs">
                <option value="">선택해주세요</option>
                {interviewers.map((i: any) => {
                  const unavailable = !i.zoom_url || !i.email;
                  const reasons = [!i.zoom_url && 'Zoom', !i.email && '이메일'].filter(Boolean).join('/');
                  return (
                    <option key={i.id} value={i.id} disabled={unavailable}>
                      {i.name} ({i.position}){unavailable ? ` — ${reasons} 미설정` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-slate-600 mb-1">날짜</label>
                <input type="date" value={confirmDate} onChange={(e) => setConfirmDate(e.target.value)}
                  className="w-full px-2 py-1.5 border rounded-lg text-xs" />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">시간</label>
                <select value={confirmTime} onChange={(e) => setConfirmTime(e.target.value)}
                  className="w-full px-2 py-1.5 border rounded-lg text-xs">
                  <option value="">시간</option>
                  {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowConfirm(false)}
                className="flex-1 px-3 py-1.5 text-xs border rounded-lg text-slate-600 hover:bg-slate-100">취소</button>
              <button onClick={handleConfirm} disabled={submitting}
                className="flex-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {submitting ? '확정 중...' : '확정 + Zoom 생성'}
              </button>
            </div>
          </div>
        )}

        {/* 재제안 폼 */}
        {showRepropose && (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <p className="text-xs font-semibold text-slate-700">일정 재제안</p>
            <DateRows dates={reproposeDates} setDates={setReproposeDates} />
            <textarea value={reproposeMessage} onChange={(e) => setReproposeMessage(e.target.value)}
              rows={2} placeholder="지원자에게 전달할 메시지..."
              className="w-full px-2 py-1.5 border rounded-lg text-xs resize-none" />
            <div className="flex gap-2">
              <button onClick={() => setShowRepropose(false)}
                className="flex-1 px-3 py-1.5 text-xs border rounded-lg text-slate-600 hover:bg-slate-100">취소</button>
              <button onClick={handleRepropose} disabled={submitting}
                className="flex-1 px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {submitting ? '제안 중...' : '재제안하기'}
              </button>
            </div>
          </div>
        )}

        {/* 평가 폼 */}
        {showEvaluate && (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <p className="text-xs font-semibold text-slate-700">면접 평가</p>
            <div>
              <label className="block text-xs text-slate-600 mb-1">점수 (0~100)</label>
              <input type="number" min="0" max="100" value={evalScore}
                onChange={(e) => setEvalScore(parseInt(e.target.value) || 0)}
                className="w-full px-2 py-1.5 border rounded-lg text-xs" />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">면접 메모</label>
              <textarea value={evalNotes} onChange={(e) => setEvalNotes(e.target.value)}
                rows={3} placeholder="면접 결과 및 메모..."
                className="w-full px-2 py-1.5 border rounded-lg text-xs resize-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowEvaluate(false)}
                className="flex-1 px-3 py-1.5 text-xs border rounded-lg text-slate-600 hover:bg-slate-100">취소</button>
              <button onClick={() => handleEvaluate(false)} disabled={submitting}
                className="flex-1 px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {submitting ? '처리 중...' : '불합격'}
              </button>
              <button onClick={() => handleEvaluate(true)} disabled={submitting}
                className="flex-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {submitting ? '처리 중...' : '합격'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

// 평판조회 동의서 섹션
const ReferenceConsentSection: React.FC<{ applicant: any; offer: any }> = ({ applicant, offer }) => {
  const [consent, setConsent] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getReferenceConsent(applicant.id)
      .then(setConsent)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [applicant.id]);

  if (loading) return null;

  const signed = consent?.signed;
  const refs = consent?.data?.references || [];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-800">📋 평판조회 동의서</h3>
        {signed ? (
          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            ✅ 서명 완료 ({consent?.signed_at ? new Date(consent.signed_at).toLocaleDateString('ko-KR') : ''})
          </span>
        ) : (
          <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
            ⏳ 미서명
          </span>
        )}
      </div>

      {signed ? (
        <div>
          {/* 추천인 목록 */}
          <div className="space-y-3 mb-4">
            {refs.map((ref: any, i: number) => (
              ref?.company || ref?.contact_name ? (
                <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-slate-700">Reference {i + 1}</span>
                    <span className="text-xs text-slate-400">{ref.current_employment ? '현직' : '전직'}</span>
                  </div>
                  <p className="text-sm text-slate-800">{ref.contact_name} ({ref.company}, {ref.relationship || ref.position})</p>
                  <p className="text-xs text-slate-500 mt-1">Tel: {ref.tel || '-'}</p>
                  {ref.additional_info && <p className="text-xs text-slate-400 mt-1">{ref.additional_info}</p>}
                </div>
              ) : null
            ))}
          </div>

          {/* PDF 다운로드 */}
          {consent?.pdf_url && (
            <button
              onClick={() => {
                const url = `${process.env.REACT_APP_API_URL || '/api'}/admin/applicants/${applicant.id}/reference-consent/pdf`;
                const token = localStorage.getItem('hire_admin_token');
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then(res => res.blob())
                  .then(blob => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `평판조회_동의서_${applicant.name}.pdf`;
                    a.click();
                  });
              }}
              className="w-full py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              📄 동의서 PDF 다운로드
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500">지원자가 아직 동의서를 작성하지 않았습니다.</p>
      )}
    </div>
  );
};

export default ApplicantDetail;
